import React, { useState, useEffect } from 'react';
import { OperationVariables, QueryResult, useLazyQuery } from '@apollo/client';
import {
  TagPicker,
  TagPickerList,
  TagPickerInput,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  Tag,
  Field,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { GetCompaniesByFilter } from '../../../utils/statApi/CompaniesApi';
import { ICompanyBase } from '../../../utils/types/ICompany';
import ITag from '../../../utils/types/ITag';

interface ICompanyPickerProps {
  selectedItems: ITag[];
  setSelectedItems: (companyGroupCompanies: ITag[]) => void;
  itemLimit?: number;
  required?: boolean;
  errorMessage?: string;
  label: string;
}

const CompanyPicker: React.FunctionComponent<ICompanyPickerProps> = ({
  selectedItems,
  setSelectedItems,
  itemLimit,
  required,
  errorMessage,
  label,
}: ICompanyPickerProps): JSX.Element => {
  const [loadCompanies] = useLazyQuery(GetCompaniesByFilter);
  const [availableOptions, setAvailableOptions] = useState<ITag[]>([]);
  const [query, setQuery] = useState('');

  // Convert selectedItems to the format expected by TagPicker v9
  const selectedOptions = (selectedItems || []).map((item) => item.key.toString());

  // Load companies when query changes with debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      if (query.length > 0) {
        loadCompanies({
          variables: {
            companySearchDto: {
              keyword: query,
            },
            paginationDto: {
              currentPage: 1,
              pageSize: 10,
            },
          },
        }).then(
          (result: QueryResult<{ companies: { items: ICompanyBase[] } }, OperationVariables>) => {
            const companiesList: ICompanyBase[] = result?.data?.companies?.items || [];
            const options = companiesList.map((node) => ({
              key: node.id.toString(),
              name: `${node.companyCode} - ${node.entityLegalName}`,
            }));
            setAvailableOptions(options);
          },
        );
      } else {
        setAvailableOptions([]);
      }
    }, 300); // Debounce delay of 300ms

    return () => {
      clearTimeout(handler);
    };
  }, [query, loadCompanies]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (e, data) => {
    if (data.value === 'no-options') {
      return;
    }

    // Check item limit
    if (itemLimit && data.selectedOptions.length > itemLimit) {
      return;
    }

    // Convert selected option keys back to full objects
    const selected = data.selectedOptions
      .map((optionKey) => {
        // Find in current selected items first
        const existing = selectedItems.find((item) => item.key === optionKey);
        if (existing) return existing;

        // Find in available options
        const available = availableOptions.find((item) => item.key === optionKey);
        if (available) return available;
        return null;
      })
      .filter((item): item is ITag => item !== null);

    setSelectedItems(selected);
    setQuery('');
  };

  // Filter options to exclude already selected items
  const filteredOptions = availableOptions.filter(
    (option) => !selectedOptions.includes(option.key.toString()),
  );

  return (
    <FluentProvider theme={webLightTheme}>
      <Field label={label} required={required} validationMessage={errorMessage}>
        <TagPicker onOptionSelect={onOptionSelect} selectedOptions={selectedOptions}>
          <TagPickerControl>
            <TagPickerGroup aria-label="Selected Companies">
              {(selectedItems || []).map((item) => (
                <Tag key={item.key} value={item.key.toString()}>
                  {item.name}
                </Tag>
              ))}
            </TagPickerGroup>
            <TagPickerInput
              aria-label="Search Companies"
              placeholder="Search Companies"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </TagPickerControl>
          <TagPickerList>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <TagPickerOption value={option.key.toString()} key={option.key}>
                  {option.name}
                </TagPickerOption>
              ))
            ) : (
              <TagPickerOption value="no-options">No results found</TagPickerOption>
            )}
          </TagPickerList>
        </TagPicker>
      </Field>
    </FluentProvider>
  );
};

export default CompanyPicker;
